import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { StorageService } from './storage.service';
import { DeviceService } from './device.service';
import { DebugLoggerService } from './debug-logger.service';
import { RevenueCatService } from './revenuecat.service';
import { ENTITLEMENT_STORAGE_KEYS } from '../constants/feature-access.constants';
import { EntitlementStatusResponse, EffectiveEntitlement, GiftRedeemResponse } from '../models/entitlement.models';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';

const SERVER_URL = 'https://ws.aforo.bink.tools:8080';

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private deviceService = inject(DeviceService);
  private debugLogger = inject(DebugLoggerService);
  private revenueCat = inject(RevenueCatService);

  private entitlementSignal = signal<EffectiveEntitlement | null>(null);
  private loadingSignal = signal<boolean>(false);

  readonly entitlement = this.entitlementSignal.asReadonly();
  readonly isLoading = this.loadingSignal.asReadonly();
  readonly isPremium = computed(() => !!this.entitlementSignal()?.premiumActive);
  readonly entitlementSource = computed(() => this.entitlementSignal()?.source || 'none');

  private async buildNetworkErrorContext(error: any, operation: string): Promise<Record<string, unknown>> {
    let connectionType: string | null = null;
    try {
      const status = await Network.getStatus();
      connectionType = status?.connectionType || null;
    } catch {
      connectionType = null;
    }

    const browserConnection = (navigator as Navigator & {
      connection?: { effectiveType?: string };
      mozConnection?: { effectiveType?: string };
      webkitConnection?: { effectiveType?: string };
    }).connection
      || (navigator as Navigator & { mozConnection?: { effectiveType?: string } }).mozConnection
      || (navigator as Navigator & { webkitConnection?: { effectiveType?: string } }).webkitConnection;

    return {
      operation,
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      connectionType,
      effectiveType: browserConnection?.effectiveType || null,
      errorName: error?.name || null,
      status: typeof error?.status === 'number' ? error.status : null,
      statusText: error?.statusText || null,
      url: error?.url || null,
      message: error?.message || String(error),
      isTrusted: error?.error?.isTrusted === true
    };
  }

  constructor() {
    this.loadCachedEntitlement();
  }

  private async loadCachedEntitlement(): Promise<void> {
    try {
      const raw = await this.storage.get(ENTITLEMENT_STORAGE_KEYS.STATE);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as EffectiveEntitlement;
      this.entitlementSignal.set(parsed);
    } catch (error) {
      this.debugLogger.warn('BillingService', 'Failed to load cached entitlement', error);
    }
  }

  private async persistEntitlement(entitlement: EffectiveEntitlement): Promise<void> {
    try {
      await this.storage.set(ENTITLEMENT_STORAGE_KEYS.STATE, JSON.stringify(entitlement));
      await this.storage.set(ENTITLEMENT_STORAGE_KEYS.LAST_SYNCED_AT, String(Date.now()));
    } catch (error) {
      this.debugLogger.error('BillingService', 'Failed to persist entitlement', error);
    }
  }

  async refreshEntitlement(): Promise<EffectiveEntitlement> {
    this.loadingSignal.set(true);

    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();

      // Check RevenueCat first on native platforms for immediate local state
      if (Capacitor.isNativePlatform() && this.revenueCat.isNativeAvailable()) {
        const rcState = await this.revenueCat.refreshAndCheckEntitlement();
        if (rcState.isActive) {
          const entitlement = this.buildEntitlementFromRevenueCat(rcState);
          this.entitlementSignal.set(entitlement);
          await this.persistEntitlement(entitlement);
          return entitlement;
        }
      }

      // Fall back to server-side entitlement check (handles gift tokens, server-tracked subscriptions)
      const response = await firstValueFrom(
        this.http.get<EntitlementStatusResponse>(`${SERVER_URL}/api/entitlements/status`, {
          params: { deviceId: deviceInfo.deviceId }
        })
      );

      if (!response?.success || !response.entitlement) {
        throw new Error('Entitlement status unavailable');
      }

      const entitlement = response.entitlement;
      this.entitlementSignal.set(entitlement);
      await this.persistEntitlement(entitlement);
      return entitlement;
    } catch (error) {
      this.debugLogger.warn(
        'BillingService',
        'Failed to refresh entitlement, using cached value',
        await this.buildNetworkErrorContext(error, 'refreshEntitlement')
      );
      return this.entitlementSignal() || this.buildDefaultEntitlement();
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Called by RevenueCat listener when customer info updates in real time.
   */
  handleRevenueCatUpdate(customerInfo: CustomerInfo): void {
    const rcState = this.revenueCat.checkEntitlement(customerInfo);
    if (rcState.isActive) {
      const entitlement = this.buildEntitlementFromRevenueCat(rcState);
      this.entitlementSignal.set(entitlement);
      this.persistEntitlement(entitlement);
    } else {
      // RC says not active; fall back to server check which may find a gift token
      this.refreshEntitlement();
    }
  }

  async presentPaywall(): Promise<void> {
    if (Capacitor.isNativePlatform() && this.revenueCat.isNativeAvailable()) {
      const result = await this.revenueCat.presentPaywall();
      if (result) {
        await this.refreshEntitlement();
      }
      return;
    }
    await this.openPlaySubscriptionPage();
  }

  async manageSubscription(): Promise<void> {
    if (Capacitor.isNativePlatform() && this.revenueCat.isNativeAvailable()) {
      await this.revenueCat.presentCustomerCenter();
      await this.refreshEntitlement();
      return;
    }
    await this.openPlaySubscriptionPage();
  }

  async restorePurchases(): Promise<void> {
    if (Capacitor.isNativePlatform() && this.revenueCat.isNativeAvailable()) {
      await this.revenueCat.restorePurchases();
      await this.refreshEntitlement();
    }
  }

  async redeemGiftToken(code: string): Promise<GiftRedeemResponse> {
    const trimmedCode = (code || '').trim();
    if (!trimmedCode) {
      return { success: false, message: 'Gift code is required' };
    }

    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();

      const response = await firstValueFrom(
        this.http.post<GiftRedeemResponse>(`${SERVER_URL}/api/entitlements/redeem-gift`, {
          code: trimmedCode,
          deviceId: deviceInfo.deviceId,
          deviceName: deviceInfo.deviceName || null,
          userDisplayName: deviceInfo.userDisplayName || null,
          userName: deviceInfo.userName || null,
          platform: deviceInfo.platform || null
        })
      );

      if (response?.success && response.entitlement) {
        this.entitlementSignal.set(response.entitlement);
        await this.persistEntitlement(response.entitlement);
      }

      return response;
    } catch (error: any) {
      this.debugLogger.error('BillingService', 'Gift redeem failed', error);
      return {
        success: false,
        message: error?.error?.message || error?.message || 'Gift redeem failed'
      };
    }
  }

  async openPlaySubscriptionPage(): Promise<void> {
    if (Capacitor.getPlatform() === 'ios') {
      return;
    }
    const packageId = 'tools.bink.aforopro';
    const url = `https://play.google.com/store/account/subscriptions?package=${encodeURIComponent(packageId)}`;

    if (!Capacitor.isNativePlatform()) {
      window.open(url, '_blank');
      return;
    }

    await Browser.open({ url });
  }

  async openPromoCodeRedeemPage(): Promise<void> {
    if (Capacitor.getPlatform() === 'ios') {
      return;
    }
    const url = 'https://play.google.com/redeem';
    if (!Capacitor.isNativePlatform()) {
      window.open(url, '_blank');
      return;
    }
    await Browser.open({ url });
  }

  private buildEntitlementFromRevenueCat(rcState: { isActive: boolean; productId?: string; expiresAt?: number | null }): EffectiveEntitlement {
    return {
      premiumActive: rcState.isActive,
      source: 'revenuecat',
      checkedAt: Date.now(),
      subscription: {
        isActive: rcState.isActive,
        productId: rcState.productId,
        expiresAt: rcState.expiresAt ?? null,
        source: 'revenuecat'
      },
      gift: {
        isActive: false,
        source: 'none'
      }
    };
  }

  private buildDefaultEntitlement(): EffectiveEntitlement {
    return {
      premiumActive: false,
      source: 'none',
      checkedAt: Date.now(),
      subscription: {
        isActive: false,
        source: 'none'
      },
      gift: {
        isActive: false,
        source: 'none'
      }
    };
  }
}
